"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";

const resourceLinks = [
  {
    href: "/entscheidung",
    label: "Entscheidungshilfe",
    description: "Kosten, Vergleiche und klare Entscheidungsgrundlagen.",
  },
  {
    href: "/technik",
    label: "Technik-Überblick",
    description: "Performance, Monitoring und sauberer SEO-Stack.",
  },
  {
    href: "/wissen",
    label: "Wissen & Artikel",
    description: "Strategie, Inhalte und laufende Updates.",
  },
  {
    href: "/warum-niemand-google-rankings-garantieren-kann",
    label: "Warum niemand Rankings garantiert",
    description: "Transparenz zu Faktoren, Wettbewerb und Algorithmus.",
  },
  {
    href: "/ssr-ssg-isr-vergleich",
    label: "SSR, SSG, ISR erklärt",
    description: "Welche Rendering-Strategie wann passt.",
  },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsResourcesOpen(false);
    }
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: "/#erfolge", label: "Erfolge" },
    { href: "/#ablauf", label: "Ablauf" },
    { href: "/#preise", label: "Preise" },
    { href: "/#vergleich", label: "Vergleich" },
    { href: "/#faq", label: "FAQ" },
    { href: "/#kontakt", label: "Kontakt" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[rgb(var(--bg-base)/0.95)] backdrop-blur-xl shadow-lg shadow-[#1B3A60]/10 border-b border-[#1B3A60]/20"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">

          <Link href="/" className="flex items-center gap-4" data-testid="link-logo">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#1B3A60]/20 to-[#3C2447]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <Image
                src="/momentrum-logo1.png"
                alt="Momentrum"
                width={240}
                height={80}
                className="h-10 md:h-12 w-auto relative drop-shadow-sm"
                priority
              />
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`link-nav-${link.label.toLowerCase()}`}
                className="text-sm font-medium transition-all text-[rgb(var(--text-muted))] hover:text-[#C9B37E]"
              >
                {link.label}
              </a>
            ))}
            <div
              className="relative"
              onMouseEnter={() => setIsResourcesOpen(true)}
              onMouseLeave={() => setIsResourcesOpen(false)}
            >
              <button
                type="button"
                className="text-sm font-medium transition-all text-[rgb(var(--text-muted))] hover:text-[#C9B37E] flex items-center gap-1"
                onClick={() => setIsResourcesOpen((open) => !open)}
                aria-expanded={isResourcesOpen}
                aria-haspopup="true"
                data-testid="button-nav-wissen"
              >
                Wissen
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isResourcesOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`absolute left-0 top-full w-[320px] rounded-2xl border border-[#1B3A60]/30 bg-[rgb(var(--bg-surface))] shadow-2xl shadow-[#1B3A60]/20 transition-all z-50 ${
                  isResourcesOpen
                    ? "opacity-100 translate-y-0 pointer-events-auto"
                    : "opacity-0 translate-y-2 pointer-events-none"
                }`}
              >
                <div className="p-4 pt-5 flex flex-col gap-3">
                  {resourceLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="group rounded-xl border border-transparent hover:border-[#1B3A60]/30 hover:bg-[rgb(var(--bg-base))] transition-colors px-4 py-3"
                    >
                      <div className="font-semibold text-[rgb(var(--text-primary))] group-hover:text-[#C9B37E]">
                        {link.label}
                      </div>
                      <div className="text-sm text-[rgb(var(--text-muted))] leading-snug">
                        {link.description}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <a
              href="#kontakt"
              data-testid="button-nav-cta"
              className="group bg-gradient-to-r from-[#1B3A60] to-[#2D5A8C] hover:from-[#2D5A8C] hover:to-[#3A6A9C] text-[rgb(var(--text-on-accent))] text-sm font-semibold px-6 py-2.5 rounded-lg transition-all shadow-lg shadow-[#1B3A60]/30 border border-[#1B3A60]/50 flex items-center gap-2"
            >
              Kostenlose Potenzial-Analyse sichern
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-[rgb(var(--text-primary))]"
            data-testid="button-mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-[rgb(var(--bg-surface))] rounded-2xl shadow-xl p-6 mb-4 border border-[#1B3A60]/30">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-[rgb(var(--text-muted))] hover:text-[#C9B37E] font-medium py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-2 border-t border-[#1B3A60]/20">
                <button
                  type="button"
                  onClick={() => setIsResourcesOpen((open) => !open)}
                  className="w-full flex items-center justify-between text-[rgb(var(--text-muted))] hover:text-[#C9B37E] font-medium py-2 transition-colors"
                  aria-expanded={isResourcesOpen}
                >
                  Wissen & Artikel
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${isResourcesOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isResourcesOpen && (
                  <div className="mt-3 flex flex-col gap-3 pl-2">
                    {resourceLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setIsResourcesOpen(false);
                        }}
                        className="text-sm text-[rgb(var(--text-muted))] hover:text-[#C9B37E] transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <a
                href="#kontakt"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-gradient-to-r from-[#1B3A60] to-[#2D5A8C] text-[rgb(var(--text-on-accent))] text-center font-semibold px-5 py-3 rounded-lg mt-2 border border-[#1B3A60]/50"
              >
                Kostenlose Potenzial-Analyse sichern
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
